/**
 * "The app pushed this history entry."
 *
 * A screen that leaves by stepping back has to know whether the entry behind
 * it belongs to the app or to whatever the user was doing before. Marking the
 * push is the only reliable way to tell: `history.length` counts the whole
 * TAB's session history and never shrinks, so a pasted link opened in a tab
 * that already has entries is indistinguishable from an in-app navigation —
 * and stepping back there walks out of the app.
 *
 * Stamp the push with `APP_PUSH_STATE`, then read the marker off the entry you
 * are standing on before deciding to `goBack`.
 */

/** Location state stamped on every entry the app pushes itself. */
export const APP_PUSH_STATE = { appPush: true } as const;

/** Whether a react-router location state came from an in-app push. */
export function isAppPushEntry(state: unknown): boolean {
  return (state as { appPush?: boolean } | null | undefined)?.appPush === true;
}

/**
 * The subscribe wall's way through to a property's General page, so an owner
 * can delete a property they never subscribed; the page shows it read-only.
 */
export function paywallPassState(propertyId: string) {
  return { ...APP_PUSH_STATE, paywallPassFor: propertyId };
}

/** The property a location state lets past the subscribe wall, if any. */
export function paywallPassFor(state: unknown): string | undefined {
  return (state as { paywallPassFor?: string } | null | undefined)
    ?.paywallPassFor;
}

/** The subset of react-router's history this module needs. */
interface LeavableHistory {
  goBack: () => void;
  location: { state?: unknown };
  replace: (path: string) => void;
}

/**
 * Leave the current screen: step back over it when the app pushed it, and
 * otherwise replace it with `fallbackPath`. Replacing rather than popping is
 * what keeps a cold-loaded link inside the app.
 */
export function leaveAppEntry(
  history: LeavableHistory,
  fallbackPath: string
): void {
  if (isAppPushEntry(history.location.state)) {
    history.goBack();
    return;
  }
  history.replace(fallbackPath);
}
